import 'primeflex/primeflex.css';
import 'primeicons/primeicons.css';
import './InstallerPanelTable.css';
import "primereact/resources/themes/lara-light-indigo/theme.css";
import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Toolbar } from 'primereact/toolbar';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { FilterMatchMode } from 'primereact/api';
import Snackbar from '../../../components/Snackbar/Snackbar';
import { exportDataTableCsv } from '../../helpers/exportCsv';
import { getClients, addClient, updateClient, deleteClient } from '../../stores/actions/installerActions';

export interface IClient {
    ID: number | null;
    name: string;
    addressStreet: string | null;
    addressBuildingNumber: string | null;
    addressPostalCode: string | null;
    addressCity: string | null;
    phone: string | null;
    email: string | null;
    nip: string | null;
}

interface ClientsProps {
    mobileView: boolean;
    clients: IClient[] | null;
    getClients: () => void;
    addClient: (client: IClient) => Promise<boolean>;
    updateClient: (client: IClient) => Promise<boolean>;
    deleteClient: (ID: number) => Promise<boolean>;
}

const emptyClient: IClient = {
    ID: null,
    name: '',
    addressStreet: null,
    addressBuildingNumber: null,
    addressPostalCode: null,
    addressCity: null,
    phone: null,
    email: null,
    nip: null,
};

// same lengths as the address fields of a system; col - width in the form on desktop (of 12)
const textFields: { field: keyof IClient; label: string; maxLength: number; col: number }[] = [
    { field: 'addressStreet', label: 'Street', maxLength: 64, col: 8 },
    { field: 'addressBuildingNumber', label: 'Building number', maxLength: 8, col: 4 },
    { field: 'addressPostalCode', label: 'Postal code', maxLength: 16, col: 4 },
    { field: 'addressCity', label: 'City', maxLength: 64, col: 8 },
    { field: 'phone', label: 'Phone', maxLength: 32, col: 6 },
    { field: 'email', label: 'E-mail', maxLength: 255, col: 6 },
    { field: 'nip', label: 'NIP', maxLength: 20, col: 6 },
];

const initialFilters = Object.fromEntries(
    ['name', ...textFields.map((f) => f.field)].map((field) => [field, { value: null, matchMode: FilterMatchMode.CONTAINS }]),
);

const Clients = ({ mobileView, clients, getClients, addClient, updateClient, deleteClient }: ClientsProps) => {
    const dt = useRef(null);
    const [client, setClient] = useState<IClient>(emptyClient);
    const [clientDialog, setClientDialog] = useState(false);
    const [deleteClientDialog, setDeleteClientDialog] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [filters, setFilters] = useState(initialFilters);
    const [notification, setNotification] = useState<{ message: string; color: 'success' | 'danger' } | null>(null);

    useEffect(() => {
        getClients();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!notification) return;
        const timeout = setTimeout(() => setNotification(null), 6000);
        return () => clearTimeout(timeout);
    }, [notification]);

    const openNew = () => {
        setClient(emptyClient);
        setSubmitted(false);
        setClientDialog(true);
    };

    const editClient = (rowData: IClient) => {
        setClient({ ...rowData });
        setSubmitted(false);
        setClientDialog(true);
    };

    const confirmDeleteClient = (rowData: IClient) => {
        setClient(rowData);
        setDeleteClientDialog(true);
    };

    const saveClient = () => {
        setSubmitted(true);
        if (!client.name?.trim()) {
            return;
        }
        // empty inputs of nullable columns are saved as NULL
        const toSave = { ...client, name: client.name.trim() };
        textFields.forEach(({ field }) => {
            (toSave as any)[field] = (toSave[field] as string)?.trim() ? toSave[field] : null;
        });
        (client.ID ? updateClient(toSave) : addClient(toSave)).then((succeeded) => {
            if (succeeded) {
                setClientDialog(false);
                setNotification({ message: client.ID ? 'Client updated' : 'Client created', color: 'success' });
            } else {
                setNotification({ message: 'Save error. A client with this name may already exist.', color: 'danger' });
            }
        });
    };

    const deleteClientItem = () => {
        deleteClient(client.ID).then((succeeded) => {
            setDeleteClientDialog(false);
            setNotification(succeeded ? { message: 'Client deleted', color: 'success' } : { message: 'Delete error', color: 'danger' });
        });
    };

    const onInputChange = (value: string, field: keyof IClient) => {
        setClient({ ...client, [field]: value });
    };

    const leftToolbarTemplate = () => (
        <div className="flex flex-wrap gap-2">
            <Button label="New" icon="pi pi-plus" severity="success" onClick={openNew} />
        </div>
    );

    const rightToolbarTemplate = () => (
        <div className="flex flex-wrap gap-2">
            <Button label="Export" icon="pi pi-file-export" outlined onClick={() => exportDataTableCsv(dt, 'clients')} />
        </div>
    );

    const actionBodyTemplate = (rowData: IClient) => (
        <React.Fragment>
            <Button icon="pi pi-pencil" rounded outlined className="mr-2" onClick={() => editClient(rowData)} />
            <Button icon="pi pi-trash" rounded outlined severity="danger" onClick={() => confirmDeleteClient(rowData)} />
        </React.Fragment>
    );

    const clientDialogFooter = (
        <React.Fragment>
            <Button label="Cancel" icon="pi pi-times" outlined onClick={() => setClientDialog(false)} />
            <Button label="Save" icon="pi pi-check" onClick={saveClient} />
        </React.Fragment>
    );

    const deleteClientDialogFooter = (
        <React.Fragment>
            <Button label="No" icon="pi pi-times" outlined onClick={() => setDeleteClientDialog(false)} />
            <Button label="Yes" icon="pi pi-check" severity="danger" onClick={deleteClientItem} />
        </React.Fragment>
    );

    return (
        <div>
            <div className="card">
                <Toolbar className="mb-4" left={leftToolbarTemplate} right={rightToolbarTemplate} />
                <DataTable ref={dt} value={Array.isArray(clients) ? clients : []} loading={clients == null} dataKey="ID" sortField="name" sortOrder={1}
                        paginator rows={25} rowsPerPageOptions={[10, 25, 50, 100]}
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} items"
                        filters={filters} onFilter={(e) => setFilters(e.filters as typeof initialFilters)} filterDisplay="row"
                        stripedRows size='small' resizableColumns exportFilename="clients">
                    <Column field="ID" header="ID" style={{ maxWidth: '80px' }} sortable />
                    <Column field="name" header="Name" sortable filter filterPlaceholder="" showFilterMenu={false} />
                    {textFields.map(({ field, label }) => (
                        <Column key={field} field={field} header={label} sortable filter filterPlaceholder="" showFilterMenu={false} />
                    ))}
                    <Column body={actionBodyTemplate} exportable={false} style={{ minWidth: '8rem' }} />
                </DataTable>
            </div>

            <Dialog visible={clientDialog}
                    style={{ width: mobileView ? '100%' : '44rem' }}
                    breakpoints={{ '960px': '75vw', '641px': '90vw' }}
                    modal className="p-fluid" footer={clientDialogFooter} onHide={() => setClientDialog(false)}
                    header={client.ID ? 'Edit client #' + client.ID : 'Add new client'}>
                <div className="formgrid grid">
                    <div className="field col-12">
                        <label className="mb-2 font-bold">Name</label>
                        <InputText value={client.name ?? ''} maxLength={255} onChange={(e) => onInputChange(e.target.value, 'name')}
                            invalid={submitted && !client.name?.trim()} autoFocus />
                        {submitted && !client.name?.trim() && <small className="p-error">Name is required.</small>}
                    </div>
                    {textFields.map(({ field, label, maxLength, col }) => (
                        <div className={`field col-12 md:col-${col}`} key={field}>
                            <label className="mb-2 font-bold">{label}</label>
                            <InputText value={(client[field] as string) ?? ''} maxLength={maxLength} onChange={(e) => onInputChange(e.target.value, field)} />
                        </div>
                    ))}
                </div>
            </Dialog>

            <Dialog visible={deleteClientDialog} style={{ width: '32rem' }} breakpoints={{ '960px': '75vw', '641px': '90vw' }}
                    header="Confirm" modal footer={deleteClientDialogFooter} onHide={() => setDeleteClientDialog(false)}>
                <div className="confirmation-content">
                    <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                    <span>
                        Are you sure you want to delete client <b>{client.name}</b>? It will be unassigned from all systems and agreements.
                    </span>
                </div>
            </Dialog>

            <Snackbar
                close
                closeNotification={() => setNotification(null)}
                color={notification?.color ?? 'success'}
                message={notification?.message ?? ''}
                open={!!notification}
                place='tc'
            />
        </div>
    );
};

const mapStateToProps = (state) => ({
    mobileView: state.dashboard.mobileView,
    clients: state.dashboard.clients,
});

const actions = {
    getClients,
    addClient,
    updateClient,
    deleteClient,
};

export default connect(mapStateToProps, actions)(Clients);
